import _ from 'lodash';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { filterActionEndpointOptions } from 'new/utils/actionEndpoints';
import type {
  ScriptCredentialField,
  ConnAuthByIdEntry,
  AuthField,
} from './SqlRunnerSourceForm.types';

export const STORAGE_TYPE_PARAM = 'parameters.storage_type';

const SQL_RUNNER_CONNECTION = CONNECTOR_CODE_SQL_RUNNER;
const VENDOR_ENDPOINT_ID_FIELD = 'vendor_endpoint_id';

/**
 * Get the single endpoint id from sql_runner source config (used for select step).
 */
export function getSqlRunnerEndpointIdFromPaging(
  paging: Record<string, unknown> | null | undefined,
): number | undefined {
  const sourceConfigFields = paging?.sourceConfigById?.[SQL_RUNNER_CONNECTION] as
    | { name: string; options?: { value: unknown }[]; values?: unknown[] }[]
    | undefined;
  if (!sourceConfigFields || !Array.isArray(sourceConfigFields)) return undefined;
  const endpointField = _.find(sourceConfigFields, (f) => f.name === VENDOR_ENDPOINT_ID_FIELD);
  // A new SQL runner source is an ETL source: never default to an action endpoint
  const endpointOptions = endpointField?.options?.length
    ? endpointField.options
    : endpointField?.values;
  const [endpointOpt] = filterActionEndpointOptions(
    endpointOptions as { value?: number; is_action?: boolean }[] | undefined,
  );
  if (endpointOpt == null || typeof endpointOpt !== 'object') return undefined;
  return 'value' in endpointOpt ? (endpointOpt as { value: number }).value : undefined;
}

export function getActiveItems(
  templates: { id: number; auth_parameters?: number[] }[] | undefined,
  items: unknown[] | undefined,
  selectedTemplate: number | null,
): unknown[] {
  const template = templates?.find((t) => t.id === selectedTemplate) || templates?.[0];
  const list = items ?? [];
  if (!templates || templates.length <= 1 || !template) return list;
  return list.filter((item: { param_id?: number }) =>
    template.auth_parameters?.includes(item.param_id as number),
  );
}

export function getScriptCredentialFieldForStorageType(
  paging: Record<string, unknown>,
  selectedConnection: string,
  templatizedScriptConfig: Record<string, unknown>,
): ScriptCredentialField | null {
  const storageType = templatizedScriptConfig?.[STORAGE_TYPE_PARAM] as string | undefined;
  if (!storageType) return null;
  const sourceConfigFields = paging?.sourceConfigById?.[selectedConnection] as
    | Record<string, unknown>[]
    | undefined;
  if (!sourceConfigFields || !Array.isArray(sourceConfigFields)) return null;
  const field = _.find(sourceConfigFields, (f: Record<string, unknown>) => {
    if (f.type !== 'script_credential') return false;
    const dep = (f.dependsOn as { allOf?: unknown[] })?.allOf ?? f.dependsOn;
    const deps = Array.isArray(dep) ? dep : dep ? [dep] : [];
    return deps.some(
      (d: { name?: string; value?: string }) =>
        d?.name === STORAGE_TYPE_PARAM && d?.value === storageType,
    );
  });
  return (field as unknown as ScriptCredentialField) ?? null;
}

export function getInitialTemplateId(paging: Record<string, unknown>): number | null {
  const connAuth = paging?.connAuthById?.[CONNECTOR_CODE_SQL_RUNNER] as
    | {
        templates?: { id: number }[];
      }
    | undefined;
  const templates = connAuth?.templates;
  return templates?.[0]?.id ?? null;
}

export type SelectStepData = {
  connectionTypes: string[];
  connectionMetas: Record<string, { name: string; displayName: string; industryCategory: string }>;
  connectionsAllowed: string[];
  endpointId: number | undefined;
  isLoading: boolean;
};

export function getSelectStepData(
  paging: Record<string, unknown> | null | undefined,
): SelectStepData {
  const sourceConfigFields = paging?.sourceConfigById?.[SQL_RUNNER_CONNECTION] as
    | {
        name: string;
        options?: { value: string; label: string }[];
        values?: unknown[];
      }[]
    | undefined;
  if (!sourceConfigFields || !Array.isArray(sourceConfigFields)) {
    return {
      connectionTypes: [],
      connectionMetas: {},
      connectionsAllowed: [],
      endpointId: undefined,
      isLoading: true,
    };
  }
  const storageTypeField = _.find(sourceConfigFields, (f) => f.name === STORAGE_TYPE_PARAM);
  const options = storageTypeField?.options || storageTypeField?.values || [];
  const endpointId = getSqlRunnerEndpointIdFromPaging(paging);
  const connectionTypes = (options as { value: string; label: string }[]).map((o) =>
    typeof o === 'object' ? o.value : (o as string),
  );
  const metaInfoById = (paging?.metaInfoById ?? {}) as Record<
    string,
    { name?: string; displayName?: string; industryCategory?: string }
  >;
  const connectionMetas: SelectStepData['connectionMetas'] = {};
  (options as { value: string; label: string }[]).forEach((opt) => {
    const value = typeof opt === 'object' ? opt.value : (opt as string);
    const label = typeof opt === 'object' ? opt.label : (opt as string);
    const fullMeta = metaInfoById[value];
    connectionMetas[value] = {
      ...(fullMeta && typeof fullMeta === 'object' ? fullMeta : {}),
      name: label,
      displayName: label,
      industryCategory: fullMeta?.industryCategory ?? 'Databases',
    };
  });
  return {
    connectionTypes,
    connectionMetas,
    connectionsAllowed: connectionTypes,
    endpointId: endpointId as number | undefined,
    isLoading: false,
  };
}

export type AuthStepConfig = {
  connectionAuthFields: AuthField[];
  connectionAuthMeta: unknown;
  connectionTypes: string[];
  connectionMetas: Record<string, unknown>;
  templates: unknown[];
};

export function getAuthStepConfig(
  paging: Record<string, unknown> | null | undefined,
  effectiveCredentialType: string | null,
  selectedTemplate: number | null,
): AuthStepConfig {
  const connAuth = effectiveCredentialType
    ? (paging?.connAuthById?.[effectiveCredentialType] as ConnAuthByIdEntry | undefined)
    : null;
  const { templates, items } = connAuth ?? {};
  const connectionAuthFields = getActiveItems(
    templates,
    items,
    selectedTemplate ?? getInitialTemplateId(paging ?? {}),
  ) as AuthField[];
  const metaById = (paging?.metaInfoById ?? {}) as Record<string, unknown>;
  const connectionMetas = effectiveCredentialType
    ? {
        ...metaById,
        [effectiveCredentialType]: metaById[effectiveCredentialType] ?? {},
      }
    : metaById;
  return {
    connectionAuthFields,
    connectionAuthMeta: effectiveCredentialType
      ? (paging?.connAuthMeta as Record<string, unknown>)?.[effectiveCredentialType]
      : null,
    connectionTypes: effectiveCredentialType ? [effectiveCredentialType] : [],
    connectionMetas,
    templates: connAuth?.templates ?? [],
  };
}
